import {openDeleteDialog} from 'platform/components';

import {useState} from 'react';

import {
  RelativeFilterConfigBody,
  RelativeFilterResponseBody,
  useCreateRelativeFilterMutation,
  useDeleteRelativeFilterMutation,
  useGetRelativeFiltersQuery,
  useUpdateRelativeFilterMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {PresetEditorDialog} from '../components/PresetEditorDialog';

type DialogState = {mode: 'closed'} | {mode: 'create'} | {mode: 'edit'; presetId: string};

export function useRelativeFiltersPresetEditor(props: TestIdProps) {
  const {data: presets = []} = useGetRelativeFiltersQuery();
  const [createRelativeFilter] = useCreateRelativeFilterMutation();
  const [updateRelativeFilter] = useUpdateRelativeFilterMutation();
  const [deleteRelativeFilter] = useDeleteRelativeFilterMutation();
  const [dialogState, setDialogState] = useState<DialogState>({mode: 'closed'});

  const openCreate = () => {
    setDialogState({mode: 'create'});
  };

  const openEdit = (presetId: string) => {
    setDialogState({mode: 'edit', presetId});
  };

  const close = () => {
    setDialogState({mode: 'closed'});
  };

  const dialogPreset: RelativeFilterResponseBody | null =
    dialogState.mode === 'edit'
      ? (presets.find((p) => p.id === dialogState.presetId) ?? null)
      : null;

  const handleSave = (name: string, config: RelativeFilterConfigBody, isDefault: boolean) => {
    if (dialogState.mode === 'create') {
      createRelativeFilter({createRelativeFilterRequestBody: {name, config, default: isDefault}})
        .unwrap()
        .then(() => setDialogState({mode: 'closed'}))
        .catch(handleApiError);
    } else if (dialogState.mode === 'edit') {
      const editedPreset = presets.find((preset) => preset.id === dialogState.presetId);

      if (editedPreset?.system) {
        updateRelativeFilter({
          id: dialogState.presetId,
          updateRelativeFilterRequestBody: {default: isDefault},
        })
          .unwrap()
          .then(() => setDialogState({mode: 'closed'}))
          .catch(handleApiError);
      } else {
        updateRelativeFilter({
          id: dialogState.presetId,
          updateRelativeFilterRequestBody: {name, config, default: isDefault},
        })
          .unwrap()
          .then(() => setDialogState({mode: 'closed'}))
          .catch(handleApiError);
      }
    }
  };

  const handleDeletePresetPress = () => {
    if (dialogState.mode !== 'edit') {
      return;
    }
    const presetId = dialogState.presetId;
    const preset = presets.find((p) => p.id === presetId);

    if (preset?.system) {
      return;
    }

    const presetName = preset?.name ?? '';
    openDeleteDialog({
      id: 'sourcing-delete-relative-filter-preset',
      text: i18n.t('entity.sourcing.relativeFilters.dialog.deleteConfirm', {
        name: presetName,
      }),
      onConfirm: () =>
        deleteRelativeFilter({id: presetId})
          .unwrap()
          .then(() => setDialogState({mode: 'closed'}))
          .catch(handleApiError),
      'data-testid': suffixTestId('deleteRelativeFilterPresetDialog', props),
    });
  };

  const presetEditorDialog = (
    <PresetEditorDialog
      key={dialogState.mode === 'edit' ? dialogState.presetId : dialogState.mode}
      isOpen={dialogState.mode !== 'closed'}
      initialPreset={dialogPreset}
      onSave={handleSave}
      onDelete={
        dialogState.mode === 'edit' && dialogPreset?.system !== true
          ? handleDeletePresetPress
          : undefined
      }
      onClose={close}
      data-testid={suffixTestId('relativeFiltersPresetEditor', props)}
    />
  );

  return {
    openCreate,
    openEdit,
    presetEditorDialog,
  };
}
